import React, {
  ChangeEvent,
  MouseEvent,
  useCallback,
  useMemo,
  useState,
} from 'react';
import useAuthenticated from '@/hooks/useAuthenticated';
import { usePopup } from '@/providers/PopupProvider/PopupProvider';
import SearchAddress from '@/ui/SearchAddress';
import { SearchAddress as SearchAddressType } from '@remodeling/common';
import { useFormContext, useWatch } from 'react-hook-form';
import { useSeqConsultContext } from '@/providers/SeqConsultProvider';

export type RadioType = 'default' | 'direct';

const useSeqSearchAddressForm = () => {
  const {
    register,
    setValue,
    watch,
    formState: { errors },
    control,
  } = useFormContext();

  const { isAuthenticated } = useAuthenticated();
  const {
    handleOpen: searchAddressPopupOpen,
    handleClose: searchAddressPopupClose,
  } = usePopup();
  const { userBasicInfo, defaultRadio, handleChangeIsDefaultAddress } =
    useSeqConsultContext();

  const [radioType, setRadioType] = useState<RadioType>(defaultRadio);
  const isShowRadioGroup = useMemo(
    () => !!userBasicInfo && userBasicInfo.simpleUserFlag === 'N',
    [userBasicInfo],
  );

  const _handleSearchAddress = useCallback(
    (address: SearchAddressType) => {
      const _address = {
        address: {
          sidoCode: address.lglSidoCd,
          houseCode: address.houseCd,
          sidoName: address.sidoNm,
          sigunguCode: address.lglSiguCd,
          sigunguName: address.siguNm,
          zoneCode: address.post5No,
          dongCode: address.lglDngCd,
          dongName: address.dngNm,
          roadnameCode: address.roadCd,
          roadnameAddress: address.addrRoad,
          buildingCode: address.bldgMgmtNo,
          buildingName: address.bldgNm,
          x: parseFloat(address.wgs84XBldgAvg),
          y: parseFloat(address.wgs84YBldgAvg),
        },
        addressDetail: '',
      };

      setValue('site.address', {
        ..._address.address,
        addressDetail: _address.addressDetail,
      });
      searchAddressPopupClose();
    },
    [searchAddressPopupClose, setValue],
  );

  const handleOpenSearchAddressPopup = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    searchAddressPopupOpen({
      popupProps: {
        popupSize: 'md',
        title: '우편번호 검색',
        children: <SearchAddress handleSearchAddress={_handleSearchAddress} />,
      },
    });
  };

  const handleChangeRadio = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const type = e.currentTarget.name as RadioType;
      setRadioType(type);
      handleChangeIsDefaultAddress(type);

      if (type === 'default') {
        setValue('site.address', userBasicInfo?.site?.address);
        setValue('site.addressDetail', userBasicInfo?.site?.addressDetail);
      } else {
        setValue('site.address', {
          sidoCode: '',
          houseCode: '',
          sidoName: '',
          sigunguCode: '',
          sigunguName: '',
          zoneCode: '',
          dongCode: '',
          dongName: '',
          roadnameCode: '',
          roadnameAddress: '',
          buildingCode: '',
          buildingName: '',
          x: '',
          y: '',
        });
        setValue('site.addressDetail', '');
      }
    },
    [
      handleChangeIsDefaultAddress,
      setValue,
      userBasicInfo?.site?.address,
      userBasicInfo?.site?.addressDetail,
    ],
  );

  const handleChangeDetailAddress = useCallback(
    (e: React.FormEvent<HTMLInputElement>) => {
      let { value } = e.currentTarget;
      if (value.length > 50) {
        value = value.slice(0, 50);
      }

      if (value.trim().length === 0) {
        setValue(
          'site.addressDetail',
          value.replace(/^\s*/, '').replace(/\s*$/, ''),
        );
      } else setValue('site.addressDetail', value);
    },
    [setValue],
  );

  const watchZoneCode = useWatch({
    name: 'site.address.zoneCode',
    control,
  });

  const watchRoadNameAddress = useWatch({
    name: 'site.address.roadnameAddress',
    control,
  });

  const watchAddressDetail = useWatch({
    name: 'site.addressDetail',
    control,
  });

  const disabledZoneCode = useMemo(() => {
    return !!watchZoneCode;
  }, [watchZoneCode]);

  const disabledRoadNameAddress = useMemo(() => {
    return !disabledZoneCode || (!!disabledZoneCode && !!watchRoadNameAddress);
  }, [disabledZoneCode, watchRoadNameAddress]);

  const disabledAddressDetail = useMemo(() => {
    if (radioType === 'default') {
      return (
        (!!watchZoneCode && !!watchAddressDetail && isShowRadioGroup) ||
        (!watchZoneCode && !watchAddressDetail)
      );
    } else {
      return !watchZoneCode && !watchAddressDetail;
    }
  }, [isShowRadioGroup, radioType, watchAddressDetail, watchZoneCode]);

  const disabledSearchButton = useMemo(() => {
    return isShowRadioGroup && radioType === 'default';
  }, [isShowRadioGroup, radioType]);

  return {
    isAuthenticated,
    handleOpenSearchAddressPopup,
    radioType,
    handleChangeRadio,
    register,
    watch,
    errors,
    disabledZoneCode,
    disabledRoadNameAddress,
    disabledAddressDetail,
    disabledSearchButton,
    isShowRadioGroup,
    handleChangeDetailAddress,
  };
};

export default useSeqSearchAddressForm;
